/**
 * The account's starred filters, and the intents that change them (LPN-68).
 *
 * One per app rather than one per window: the list is the account's, every window's star
 * reads the same one, and a star added from one window has to show as starred in the next
 * window's dialog without either of them re-reading anything.
 *
 * Persisted the way [WorkspaceBackingViewModel] persists the workspace — a blob under its
 * own UI-settings key ([UiSettingKeys.STARRED_FILTERS]), written through
 * [StorageRepository.putSetting] and re-read on every settings revision, so a star added in
 * another tab or on another device appears here as soon as the server says so.
 *
 * What a starred filter holds and how two are compared is [StarredFilters.kt]'s business;
 * this file only holds the list and writes it back.
 */
package se.soderbjorn.lunapin.client.viewmodel

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import kotlin.coroutines.cancellation.CancellationException
import se.soderbjorn.lunapin.client.StorageRepository
import se.soderbjorn.lunapin.clientserver.UiSettingKeys

class StarredFiltersBackingViewModel(
    private val storage: StorageRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main),
) {
    private val _stateFlow = MutableStateFlow(State())

    /** The starred filters, render-ready. */
    val stateFlow = _stateFlow.asStateFlow()

    /**
     * Read the stored list, and read it again whenever the account or its settings change.
     * Called once by the platform and never returns — [WorkspaceBackingViewModel.run] is
     * the same loop and says why each of its two inputs is there.
     */
    @Throws(CancellationException::class, Exception::class)
    suspend fun run() {
        combine(
            storage.sessionFlow.map { it?.user?.id }.distinctUntilChanged(),
            storage.settingsRevisionFlow,
        ) { _, _ -> Unit }
            .collectLatest {
                val starred = StarredFiltersCodec.decode(storage.setting(UiSettingKeys.STARRED_FILTERS))
                _stateFlow.value = State(starred = starred, isRestored = true)
            }
    }

    /** Whether [filter] is starred, ignoring its column count. */
    fun isStarred(filter: WindowContent.BookmarkList): Boolean =
        _stateFlow.value.starred.containsFilter(filter)

    /**
     * *Add to starred* / *Remove from starred* — and ⌘S pressed inside the dialog.
     *
     * Un-stars [filter] when it is starred, stars it otherwise.
     *
     * @param windowName the window's own name, if the user gave it one. It becomes the
     *   star's label; without one the label is the filter's short summary, which is what
     *   the window is called in the sidebar anyway.
     */
    fun onStarToggled(filter: WindowContent.BookmarkList, windowName: String? = null) {
        val current = _stateFlow.value.starred
        if (current.containsFilter(filter)) {
            commit(current.withoutFilter(filter))
        } else {
            val label = windowName?.trim()?.takeIf { it.isNotEmpty() } ?: filterSummary(filter)
            commit(current.withFilter(filter, label))
        }
    }

    /** A row's own × — un-star [filter] without opening it first. */
    fun onStarRemoved(filter: WindowContent.BookmarkList) {
        commit(_stateFlow.value.starred.withoutFilter(filter))
    }

    /**
     * Publish [next] and write it back. Refuses before the first read, for the workspace's
     * reason: an intent arriving then would write a list built from nothing over the real one.
     */
    private fun commit(next: List<StarredFilter>) {
        val state = _stateFlow.value
        if (!state.isRestored || next == state.starred) return
        _stateFlow.value = state.copy(starred = next)
        val blob = StarredFiltersCodec.encode(next)
        scope.launch { storage.putSetting(UiSettingKeys.STARRED_FILTERS, blob) }
    }

    /**
     * @property starred the starred filters, in the order they were starred.
     * @property isRestored false until [run] has read the stored list.
     */
    data class State(
        val starred: List<StarredFilter> = emptyList(),
        val isRestored: Boolean = false,
    ) {
        /** One row per starred filter: its label, and the filter read back in full. */
        val rows: List<Row>
            get() = starred.map { Row(it.label, filterSummary(it.filter, FilterSummaryForm.LONG), it.filter) }
    }

    /**
     * One row of the Starred dialog.
     *
     * @property label the star's label.
     * @property summary the filter in words, long form — shown under the label, so two
     *   stars that happen to share a label can still be told apart.
     * @property filter what picking the row applies.
     */
    data class Row(
        val label: String,
        val summary: String,
        val filter: WindowContent.BookmarkList,
    )
}
